import { useState, useRef } from "react"
import { useFirestore } from "reactfire"
import { collection, addDoc, doc, updateDoc } from "firebase/firestore"
import { ETAPAS_POR_UNIDAD } from "../config/etapas"
import { UNIDADES_CON_ROL, UNIDADES_SCOUT } from "../config/unidades"
import "../styles/Dashboard.css"
import { useActividad } from "../hooks/useActividad"

export default function ApoderadoModal({ unidadId, apoderado, onCerrar }) {
  const firestore  = useFirestore()
  const { registrar } = useActividad()
  const editando   = !!apoderado
  const esConsejo  = UNIDADES_CON_ROL.includes(unidadId)
  const fileRef    = useRef(null)

  const [form, setForm] = useState({
    nombre:      apoderado?.nombre             || "",
    scouts:      apoderado?.scouts?.join(", ") || "",
    exento:      apoderado?.exento             || false,
    etapa:       apoderado?.etapa              || "",
    unidadRol:   apoderado?.unidadRol          || "",
    fichaMedica: apoderado?.fichaMedica        || "",
    descripcion: apoderado?.descripcion        || "",
    fotoUrl:     apoderado?.fotoUrl            || "",
    telefono:    apoderado?.telefono           || "",
    inscrito:    apoderado?.inscrito           ?? false,
  })
  const [fotoPreview,  setFotoPreview]  = useState(apoderado?.fotoUrl || null)
  const [guardando,    setGuardando]    = useState(false)
  const [error,        setError]        = useState("")

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setForm(f => ({ ...f, [name]: type === "checkbox" ? checked : value }))
    setError("")
  }

  const handleFoto = (e) => {
    const file = e.target.files[0]
    if (!file) return

    // Comprimir y convertir a Base64 para guardar en Firestore
    const canvas  = document.createElement("canvas")
    const ctx     = canvas.getContext("2d")
    const img     = new Image()
    const reader  = new FileReader()

    reader.onload = (ev) => {
      img.onload = () => {
        // Redimensionar a máximo 200x200px
        const MAX = 200
        const ratio = Math.min(MAX / img.width, MAX / img.height)
        canvas.width  = img.width  * ratio
        canvas.height = img.height * ratio
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
        const base64 = canvas.toDataURL("image/jpeg", 0.75)
        setFotoPreview(base64)
        setForm(f => ({ ...f, fotoUrl: base64 }))
      }
      img.src = ev.target.result
    }
    reader.readAsDataURL(file)
  }

  const guardar = async () => {
    if (!form.nombre.trim()) { setError("El nombre es obligatorio."); return }
      setGuardando(true)
    setError("")

    const scouts = form.scouts.split(",").map(s => s.trim()).filter(Boolean)
    const data = {
      nombre:      form.nombre.trim(),
      scouts,
      exento:      form.exento,
      fichaMedica: form.fichaMedica || null,
      descripcion: form.descripcion.trim() || null,
      fotoUrl:     form.fotoUrl || null,
      telefono:    form.telefono.trim() || null,
      inscrito:    form.inscrito,
      // Consejo usa unidadRol, el resto usa etapa
      ...(esConsejo
        ? { unidadRol: form.unidadRol || null, etapa: null }
        : { etapa: form.etapa || null, unidadRol: null }
      ),
    }

    try {
      if (editando) {
        await updateDoc(doc(firestore, "unidades", unidadId, "apoderados", apoderado.id), data)
        await registrar({ accion: "editó", entidad: "apoderado", nombre: data.nombre, unidadId })
      } else {
        await addDoc(collection(firestore, "unidades", unidadId, "apoderados"), data)
        await registrar({ accion: "creó", entidad: "apoderado", nombre: data.nombre, unidadId })
      }
      onCerrar()
    } catch (err) {
      setError("Error al guardar: " + err.message)
    } finally {
      setGuardando(false)
    }
  }

  const etapas = ETAPAS_POR_UNIDAD[unidadId] || []

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onCerrar()}>
      <div className="modal-box" style={{ maxWidth: 560 }}>
        <button className="modal-cerrar" onClick={onCerrar}>×</button>
        <h2 className="modal-titulo">
          {editando
            ? esConsejo ? "Editar miembro del Consejo" : "Editar Apoderado"
            : esConsejo ? "Nuevo miembro del Consejo" : "Nuevo Apoderado"
          }
        </h2>

        {/* ── Foto ── */}
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10, marginBottom: 24 }}>
          <div
            onClick={() => fileRef.current?.click()}
            style={{
              width: 96, height: 96, borderRadius: "50%",
              background: "var(--verde-suave)", border: "2px dashed var(--verde-claro)",
              display: "flex", alignItems: "center", justifyContent: "center",
              cursor: "pointer", overflow: "hidden", position: "relative",
            }}
          >
            {fotoPreview
              ? <img src={fotoPreview} alt="foto" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              : <span style={{ fontSize: 32, opacity: 0.5 }}>📷</span>
            }

          </div>
          <button
            type="button"
            className="btn-secondary"
            style={{ fontSize: 12, padding: "6px 16px" }}
            onClick={() => fileRef.current?.click()}
          >
            {fotoPreview ? "Cambiar foto" : "Subir foto"}
          </button>
          <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleFoto} />
        </div>

        {/* ── Nombre ── */}
        <div className="form-group">
          <label className="form-label">
            {esConsejo ? "Nombre del dirigente / guiadora" : "Nombre del Apoderado"}
          </label>
          <input
            className="form-input" name="nombre" value={form.nombre}
            onChange={handleChange}
            placeholder={esConsejo ? "Ej: Carlos Pérez" : "Ej: María González"}
          />
        </div>

        {/* ── Scouts / Cargo ── */}
        <div className="form-group">
          <label className="form-label">
            {esConsejo ? "Cargo o rol" : <>Scouts que representa <span style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>(separados por coma)</span></>}
          </label>
          <input
            className="form-input" name="scouts" value={form.scouts}
            onChange={handleChange}
            placeholder={esConsejo ? "Ej: Jefe de Grupo, Tesorero" : "Ej: Pedro González, Ana González"}
          />
        </div>

        {/* ── Etapa (unidades normales) / Unidad (consejo) + Ficha médica ── */}
        <div className="form-row">
          <div className="form-group">
            {esConsejo ? (
              <>
                <label className="form-label">Unidad</label>
                <select className="form-select" name="unidadRol" value={form.unidadRol} onChange={handleChange}>
                  <option value="">— Sin asignar —</option>
                  {UNIDADES_SCOUT.map(u => (
                    <option key={u.value} value={u.value}>{u.label}</option>
                  ))}
                </select>
              </>
            ) : (
              <>
                <label className="form-label">Etapa</label>
                <select className="form-select" name="etapa" value={form.etapa} onChange={handleChange}>
                  <option value="">— Sin asignar —</option>
                  {etapas.map(e => (
                    <option key={e.value} value={e.value}>{e.label}</option>
                  ))}
                </select>
              </>
            )}
          </div>
          <div className="form-group">
            <label className="form-label">Ficha Médica</label>
            <select className="form-select" name="fichaMedica" value={form.fichaMedica} onChange={handleChange}>
              <option value="">— Sin asignar —</option>
              <option value="actualizada">Actualizada</option>
              <option value="desactualizada">Desactualizada</option>
            </select>
          </div>
        </div>

        {/* ── Teléfono + Inscrito ── */}
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Teléfono</label>
            <input
              className="form-input" name="telefono" value={form.telefono}
              onChange={handleChange}
              placeholder="Ej: +56 9 1234 5678"
              type="tel"
            />
          </div>
          <div className="form-group">
            <label className="form-label">Inscrito</label>
            <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
              <button
                type="button"
                onClick={() => setForm(f => ({ ...f, inscrito: true }))}
                style={{
                  flex: 1, padding: "9px 0", borderRadius: 10, border: "2px solid",
                  cursor: "pointer", fontWeight: 700, fontSize: 13, transition: "all 0.15s",
                  borderColor: form.inscrito ? "#16a34a"        : "var(--gris-borde)",
                  background:  form.inscrito ? "#dcfce7"        : "#fff",
                  color:       form.inscrito ? "#16a34a"        : "var(--gris)",
                }}
              >
                ✓ Sí
              </button>
              <button
                type="button"
                onClick={() => setForm(f => ({ ...f, inscrito: false }))}
                style={{
                  flex: 1, padding: "9px 0", borderRadius: 10, border: "2px solid",
                  cursor: "pointer", fontWeight: 700, fontSize: 13, transition: "all 0.15s",
                  borderColor: !form.inscrito ? "#dc2626"       : "var(--gris-borde)",
                  background:  !form.inscrito ? "#fee2e2"       : "#fff",
                  color:       !form.inscrito ? "#dc2626"       : "var(--gris)",
                }}
              >
                ✕ No
              </button>
            </div>
          </div>
        </div>

        {/* ── Descripción / Notas ── */}
        <div className="form-group">
          <label className="form-label">
            {esConsejo ? "Notas sobre el dirigente" : "Notas sobre el scout"}
            <span style={{ fontWeight: 400, marginLeft: 6, textTransform: "none", letterSpacing: 0, color: "var(--gris)" }}>
              (opcional)
            </span>
          </label>
          <textarea
            className="form-input"
            name="descripcion"
            value={form.descripcion}
            onChange={handleChange}
            placeholder={esConsejo
              ? "Ej: Carlos lleva 10 años en el grupo, especialista en campamentos..."
              : "Ej: Gustavo tiene 5 años, le gusta jugar con sus amigos y es muy sociable..."
            }
            rows={3}
            style={{ resize: "vertical", minHeight: 72 }}
          />
        </div>

        {/* ── Exento ── */}
        <label className={`exento-toggle ${form.exento ? "activo" : ""}`}>
          <input type="checkbox" name="exento" checked={form.exento} onChange={handleChange} />
          <div>
            <strong>Exento de pago</strong>
            <div style={{ fontSize: 12, color: "var(--gris)", marginTop: 2 }}>
              No afecta el resumen financiero de la unidad.
            </div>
          </div>
        </label>

        {error && <div style={{ color: "var(--rojo)", fontSize: 13, marginBottom: 12 }}>⚠ {error}</div>}

        <div className="form-actions">
          <button className="btn-secondary" onClick={onCerrar}>Cancelar</button>
          <button className="btn-primary" onClick={guardar} disabled={guardando}>
            {guardando ? "Guardando..." : editando ? "Guardar Cambios" : esConsejo ? "Agregar miembro" : "Crear Apoderado"}
          </button>
        </div>
      </div>
    </div>
  )
}
